import { useMemo } from 'react';
import { createSearchParams,
  Navigate,
  NavigateFunction,
  NavLink,
  useLocation,
  useNavigate } from 'react-router-dom';
import { AnyObject } from '@triare/auth-redux';
import { BarsOutlined,
  FileTextOutlined,
  GlobalOutlined,
  MessageOutlined,
  ReconciliationOutlined,
  ShoppingOutlined,
  SolutionOutlined,
  TagsOutlined,
  UsergroupAddOutlined,
  AppstoreOutlined,
  UserOutlined,
  StarOutlined } from '@ant-design/icons';
import { Menu as AntdMenu } from 'antd';
import { useSelector } from 'react-redux';

import { ItemType, MenuItemType } from 'antd/es/menu/interface';
import { Route } from '../../../routes';
import { RootState } from '../../../store';
import { moduleName, User } from '../../../store/auth';
import { useSimpleModal } from '../../Common/Modal/Simple';
import NotificationDot from '../../Common/NotificationDot';
import { useNewTicketsIndicator } from '../../../hooks/useNewTicketsIndicator';
import { doesUserHaveAccess } from '../../../routes/PrivateRoute';

import Topics from '../../../pages/Topics';
import TopicCreate from '../../../pages/Topics/Create';
import Users from '../../../pages/Users';
import UserProfile from '../../../pages/Users/[userId]';
import UserUpdate from '../../../pages/Users/[userId]/Update';
import Admins from '../../../pages/Admins';
import CategoriesPage from '../../../pages/Categories';
import Businesses from '../../../pages/Businesses';
import Articles from '../../../pages/Articles';
import Reports from '../../../pages/Reports';
import Comments from '../../../pages/Comments';
import Posts from '../../../pages/Posts';
import Tickets from '../../../pages/Tickets';
import Pages from '../../../pages/Pages';
import PageCreate from '../../../pages/Pages/Create';
import PagesUpdate from '../../../pages/Pages/[pageId]/Update';
import Internationals from '../../../pages/Internationals';
import InternationalsCreate from '../../../pages/Internationals/Create';
import InternationalsUpdate from '../../../pages/Internationals/[internationalId]/Update';
import SubCategoryCreatePage from '../../../pages/Categories/[categoryId]/SubCategories/Create';
import SubCategoryUpdatePage from '../../../pages/Categories/[categoryId]/SubCategories/[subCategoryId]/Update';
import CategoryCreatePage from '../../../pages/Categories/Create';
import CategoryUpdatePage from '../../../pages/Categories/[categoryId]/Update';
import AdminsCreate from '../../../pages/Admins/Create';
import AdminsProfile from '../../../pages/Admins/[adminId]';
import AdminsUpdate from '../../../pages/Admins/[adminId]/Update';
import BusinessCreatePage from '../../../pages/Businesses/Create';
import BusinessUpdatePage from '../../../pages/Businesses/[businessId]/Update';
import BusinessView from '../../../pages/Businesses/[businessId]';
import ArticleUpdate from '../../../pages/Articles/[articleId]/Update';
import ArticleView from '../../../pages/Articles/[articleId]';
import ArticleCreate from '../../../pages/Articles/Create';
import UsersCreate from '../../../pages/Users/Create';
import ReportView from '../../../pages/Reports/[reportId]';
import CommentUpdate from '../../../pages/Comments/[commentId]/Update';
import TicketView from '../../../pages/Tickets/[ticketId]';
import SubCategoryQuestionnaire from
  '../../../pages/Categories/[categoryId]/SubCategories/[subCategoryId]/Update/Questionnaire';
import TopicUpdate from '../../../pages/Topics/[topicId]/Update';
import styles from './index.module.scss';
import States from '../../../pages/States';
import StateUpdate from '../../../pages/States/[stateId]/Update';

export type MenuNotification = 'tickets';

export type MenuNotifications = Partial<Record<MenuNotification, boolean>>;

export interface MenuRoute extends Route {
  ignoreMenu?: boolean;
  notification?: MenuNotification;

  children?: MenuRoute[];
}

// Reusable children for article types
const articleChildren: MenuRoute[] = [
  {
    ignoreMenu: true,
    privateRoute: true,
    name: 'Article Create',
    route: {
      path: 'create',
      element: <ArticleCreate />,
    },
  },
  {
    ignoreMenu: true,
    privateRoute: true,
    name: 'Article Profile',
    route: {
      path: ':articleId',
      element: <ArticleView />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Article Update',
        route: {
          path: 'update',
          element: <ArticleUpdate />,
        },
      },
    ],
  },
];

export const routes: MenuRoute[] = [
  {
    privateRoute: true,
    name: 'Admin List',
    icon: <UserOutlined />,
    align: 'left',
    route: {
      path: 'admins',
      element: <Admins />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Admin Create',
        route: {
          path: 'create',
          element: <AdminsCreate />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Admin Profile',
        route: {
          path: ':userId',
          element: <AdminsProfile />,
        },
        children: [
          {
            ignoreMenu: true,
            privateRoute: true,
            name: 'Admin Update',
            route: {
              path: 'update',
              element: <AdminsUpdate />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    name: 'Users',
    icon: <UsergroupAddOutlined />,
    align: 'left',
    route: {
      path: 'users',
      element: <Users />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'User Create',
        route: {
          path: 'create',
          element: <UsersCreate />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'User Profile',
        route: {
          path: ':userId',
          element: <UserProfile />,
        },
        children: [
          {
            ignoreMenu: true,
            privateRoute: true,
            name: 'User Update',
            route: {
              path: 'update',
              element: <UserUpdate />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    name: 'Tipping categories',
    icon: <TagsOutlined />,
    align: 'left',
    route: {
      path: 'categories',
      element: <CategoriesPage />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Tipping category create',
        route: {
          path: 'create',
          element: <CategoryCreatePage />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Tipping category',
        route: {
          path: ':categoryId',
          element: <Navigate to="update" replace />,
        },
        children: [
          {
            ignoreMenu: true,
            privateRoute: true,
            name: 'Tipping category update',
            route: {
              path: 'update',
              element: <CategoryUpdatePage />,
            },
          },
          {
            ignoreMenu: true,
            privateRoute: true,
            name: 'Tipping sub categories',
            route: {
              path: 'sub-categories',
            },
            children: [
              {
                ignoreMenu: true,
                privateRoute: true,
                name: 'Tipping sub category create',
                route: {
                  path: 'create',
                  element: <SubCategoryCreatePage />,
                },
              },
              {
                ignoreMenu: true,
                privateRoute: true,
                name: 'Tipping sub category',
                route: {
                  path: ':subCategoryId',
                  element: <Navigate to="update" replace />,
                },
                children: [
                  {
                    ignoreMenu: true,
                    privateRoute: true,
                    name: 'Tipping sub category update',
                    route: {
                      path: 'update',
                      element: <SubCategoryUpdatePage />,
                    },
                    children: [
                      {
                        ignoreMenu: true,
                        privateRoute: true,
                        name: 'Questionnaire',
                        route: {
                          path: 'questionnaire/:userType',
                          element: <SubCategoryQuestionnaire />,
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    name: 'Topics',
    icon: <AppstoreOutlined />,
    align: 'left',
    route: {
      path: 'topics',
      element: <Topics />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Topic Create',
        route: {
          path: 'create',
          element: <TopicCreate />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Topic',
        route: {
          path: ':topicId/update',
          element: <TopicUpdate />,
        },
      },
    ],
  },
  {
    privateRoute: true,
    name: 'Businesses',
    icon: <ShoppingOutlined />,
    align: 'left',
    route: {
      path: 'businesses',
      element: <Businesses />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Business create',
        route: {
          path: 'create',
          element: <BusinessCreatePage />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Business Profile',
        route: {
          path: ':businessId',
          element: <BusinessView />,
        },
        children: [
          {
            ignoreMenu: true,
            privateRoute: true,
            name: 'Business update',
            route: {
              path: 'update',
              element: <BusinessUpdatePage />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    name: 'Articles',
    icon: <FileTextOutlined />,
    align: 'left',
    route: {
      path: 'articles',
      // element: <Navigate to="blog" replace />,
    },
    children: [
      {
        privateRoute: true,
        name: 'Blog',
        route: {
          path: 'blog',
          element: <Articles />,
        },
        children: articleChildren,
      },
      {
        privateRoute: true,
        name: 'History of tipping',
        route: {
          path: 'history',
          element: <Articles />,
        },
        children: articleChildren,
      },
      {
        privateRoute: true,
        name: 'Psychology of tipping',
        route: {
          path: 'psychology',
          element: <Articles />,
        },
        children: articleChildren,
      },
    ],
  },
  {
    privateRoute: true,
    name: 'States',
    icon: <StarOutlined />,
    align: 'left',
    route: {
      path: 'states',
      element: <States />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Topic',
        route: {
          path: ':stateId/update',
          element: <StateUpdate />,
        },
      },
    ],
  },
  {
    privateRoute: true,
    name: 'Reports',
    icon: <ReconciliationOutlined />,
    align: 'left',
    route: {
      path: 'reports',
      element: <Reports />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Report View',
        route: {
          path: ':reportId',
          element: <ReportView />,
        },
      },
    ],
  },
  {
    privateRoute: true,
    name: 'Comments',
    icon: <MessageOutlined />,
    align: 'left',
    route: {
      path: 'comments',
      element: <Comments />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Comment Update',
        route: {
          path: ':commentId/update',
          element: <CommentUpdate />,
        },
      },
    ],
  },
  {
    privateRoute: true,
    name: 'Posts',
    icon: <MessageOutlined />,
    align: 'left',
    route: {
      path: 'posts',
      element: <Posts />,
    },
  },
  {
    privateRoute: true,
    name: 'Tickets',
    icon: <SolutionOutlined />,
    align: 'left',
    notification: 'tickets',
    route: {
      path: 'tickets',
      element: <Tickets />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Tickets View',
        route: {
          path: ':ticketId',
          element: <TicketView />,
        },
      },
    ],
  },
  {
    privateRoute: true,
    name: 'International page',
    icon: <GlobalOutlined />,
    align: 'left',
    route: {
      path: 'internationals',
      element: <Internationals />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'International create',
        route: {
          path: 'create',
          element: <InternationalsCreate />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'International update',
        route: {
          path: ':internationalId',
          element: <InternationalsUpdate />,
        },
      },
    ],
  },
  {
    privateRoute: true,
    name: 'Pages',
    icon: <BarsOutlined />,
    align: 'left',
    route: {
      path: 'pages',
      element: <Pages />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Page create',
        route: {
          path: 'create',
          element: <PageCreate />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: 'Page update',
        route: {
          path: ':pageId',
          element: <PagesUpdate />,
        },
      },
    ],
  },
];

export const paramsToString = (user?: User | null, params?: ((user: User) => string) | AnyObject | string) => {
  if (!user) {
    return '';
  }

  if (typeof params === 'string') {
    return '?params';
  }

  if (typeof params === 'function') {
    let result = params(user);

    if (result) {
      if (typeof result === 'object') {
        result = createSearchParams(result).toString();
      }

      return `?${result}`;
    }

    return '';
  }

  if (params && typeof params === 'object') {
    return `?${createSearchParams(params).toString()}`;
  }

  return '';
};

const createItem = (
  navigate: NavigateFunction,
  { route, children, ignoreMenu, privateRoute, notification, ...data }: MenuRoute,
  user: User,
  notifications: MenuNotifications,
  parentPath = '',
): any | ItemType<MenuItemType>[] | undefined => {
  const currentFullPath = [parentPath, route?.path]
    .filter(Boolean)
    .join('/')
    .replace(/\/+/g, '/'); // Use a global replace to handle multiple slashes

  let childrenList;

  if (children && children.length > 0) {
    childrenList = children.filter(({ ignoreMenu: childrenIgnoreMenu }) => childrenIgnoreMenu !== true);

    if (childrenList.length > 0) {
      childrenList = childrenList.map(
        ({
          ignoreMenu: childrenIgnoreMenu,
          privateRoute: childrenPrivateRoute,
          children: subChildren,
          ...child
        }) => createItem(navigate, { ...child, children: subChildren }, user, notifications, currentFullPath),
      );
    }
  }

  const name = typeof data.name === 'function' ? data.name(user) : data.name;
  const icon = typeof data.icon === 'function' ? data.icon(user) : data.icon;
  const hasNotification = !!(notification && notifications[notification]);

  return {
    ...data,
    // The label is hidden when the menu is collapsed, so the icon carries its own dot for that case.
    icon: hasNotification && icon ? (
      <span className={styles.iconWithDot}>
        {icon}
        <NotificationDot title={`New ${name}`} className={styles.iconDot} />
      </span>
    ) : icon,
    children: childrenList && childrenList.length > 0 ? childrenList : undefined,
    label: route && (!childrenList || childrenList.length === 0) ? (
      // Use the calculated full path for the NavLink
      <NavLink to={`/${currentFullPath}${paramsToString(user, route.params) || ''}`}>
        {name}
        {hasNotification ? <NotificationDot title={`New ${name}`} /> : null}
      </NavLink>
    ) : (
      name
    ),
    // Use the calculated full path for the key to ensure correct highlighting
    key: route ? `/${currentFullPath}` : `/${data.key || name}`,
  };
};

export const menuLeft = routes.filter(({ ignoreMenu }) => ignoreMenu !== true).filter(({ align }) => align === 'left');

export const menuRight = routes
  .filter(({ ignoreMenu }) => ignoreMenu !== true)
  .filter(({ align }) => align === 'right');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filter(list: any[], user: User) {
  const newList = list.map((item) => ({
    ...item,
    children: item.children ? [...item.children] : undefined,
  }));

  return newList.filter((item) => {
    if (item && item.children && item.children.length > 0) {
      // eslint-disable-next-line no-param-reassign
      item.children = filter(item.children, user);
    }

    return item?.roles ? doesUserHaveAccess(item?.roles, user) : true;
  });
}

export default function Menu(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => ({
    user: state[moduleName].user,
  }));
  const { contextHolder } = useSimpleModal();
  const { pathname } = useLocation();
  const hasNewTickets = useNewTicketsIndicator();
  const notifications = useMemo<MenuNotifications>(() => ({ tickets: hasNewTickets }), [hasNewTickets]);
  const menuLeftFiltered = useMemo(
    () => filter(
      menuLeft.map((data) => createItem(navigate, data, user as User, notifications)),
      user as User,
    )
      .map((item) => ({
        ...item,
        name: typeof item.name === 'function' ? item.name(user as User) : item.name,
        icon: typeof item.icon === 'function' ? item.icon(user as User) : item.icon,
      })),
    [user, navigate, notifications],
  );

  // const activeMenuItem = pathname === '/' ? menuLeftFiltered[0]?.key : `/${pathname.split('/')[1]}`;

  // We need to find the menu key that best matches the current URL.
  // For example, if the URL is '/users/create', the best match is the key '/users'.
  const activeKey = useMemo(() => {
    // 1. Recursively collect all possible keys from our generated menu items.
    const allKeys: string[] = [];
    const collectKeys = (items: any[]) => {
      for (const item of items) {
        if (item.key) {
          allKeys.push(item.key);
        }
        if (item.children) {
          collectKeys(item.children);
        }
      }
    };

    collectKeys(menuLeftFiltered);

    // 2. Filter the keys to find all that are a prefix of the current URL,
    //    then sort them by length descending to find the longest (most specific) match.
    const bestMatch = allKeys
      .filter((key) => pathname.startsWith(key))
      .sort((a, b) => b.length - a.length)[0];

    return bestMatch || '';
  }, [pathname, menuLeftFiltered]);

  // const openKeys = [`/${pathname.split('/')[1]}`];
  const openKeys = activeKey ? [`/${activeKey.split('/')[1]}`] : [];

  return (
    <div className={styles.menu}>
      <AntdMenu
        theme="dark"
        mode="inline"
        defaultSelectedKeys={[activeKey]}
        // selectedKeys={[pathname]}
        defaultOpenKeys={openKeys}
        selectedKeys={[activeKey]}
        items={menuLeftFiltered}
        style={{ minWidth: 0, flex: 'auto' }}
      />
      {contextHolder}
    </div>
  );
}
